"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactInformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className="w-full bg-[hsl(215,12%,25%)] py-16 md:py-24">
      <div className={`container mx-auto px-4 max-w-2xl transition-all duration-[150ms] ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="mb-8 text-center">
          <h2 className="text-white font-display text-3xl mb-3">Let's talk through your project</h2>
          <p className="text-white/80 text-base leading-relaxed">
            Share what you're thinking about, and we'll start a thoughtful conversation about how we might work together.
          </p>
        </div>
        
        <FormProvider className="w-full" formId="contact-form-contact-page" sectionName="contact-information" defaultValues={{
        name: "",
        email: "",
        town: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        town: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        message: {
          required: true,
          minLength: 10,
          maxLength: 1000
        }
      }} submitText="Start a Conversation" successMessage="Thank you for reaching out. We'll be in touch soon to discuss your project.">
          <ContactFormFields />
        </FormProvider>
      </div>
    </section>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[hsl(190,55%,95%)] border border-[hsl(190,55%,45%)] rounded-md">
        <p className="text-[hsl(0,0%,20%)] text-base leading-relaxed mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[hsl(190,55%,45%)] text-[hsl(190,55%,45%)] hover:bg-[hsl(190,55%,45%)] hover:text-white">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      },
      maxLength: {
        value: 100,
        message: "Name must be less than 100 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white text-base">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[hsl(190,55%,45%)] h-12 px-4" />
            </FormControl>
            <FormMessage className="text-[hsl(15,65%,55%)]" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white text-base">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" {...field} className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[hsl(190,55%,45%)] h-12 px-4" />
            </FormControl>
            <FormMessage className="text-[hsl(15,65%,55%)]" />
          </FormItem>} />

      <FormField control={control} name="town" rules={{
      required: "Town/Location is required",
      minLength: {
        value: 2,
        message: "Town must be at least 2 characters"
      },
      maxLength: {
        value: 100,
        message: "Town must be less than 100 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white text-base">Town or Location</FormLabel>
            <FormControl>
              <Input placeholder="Belgrade, Manchester, etc." {...field} className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[hsl(190,55%,45%)] h-12 px-4" />
            </FormControl>
            <FormMessage className="text-[hsl(15,65%,55%)]" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      },
      maxLength: {
        value: 1000,
        message: "Message must be less than 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white text-base">Tell us a bit about your project</FormLabel>
            <FormControl>
              <textarea placeholder="Share your project ideas, timeline, or any questions you have..." {...field} rows={6} className="flex w-full rounded-md bg-white/10 border border-white/30 px-4 py-3 text-base text-white placeholder:text-white/50 focus:border-[hsl(190,55%,45%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(190,55%,45%)]/50 disabled:cursor-not-allowed disabled:opacity-50 resize-none" />
            </FormControl>
            <FormMessage className="text-[hsl(15,65%,55%)]" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-[hsl(15,65%,95%)] border border-[hsl(15,65%,55%)] rounded-md">
          <p className="text-[hsl(15,65%,55%)] text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-[hsl(190,55%,45%)] hover:bg-[hsl(190,55%,50%)] text-white font-semibold text-base">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : 'Start a Conversation'}
      </Button>
    </Form>;
}